import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api, type Account, type GoogleStatus, type FamilyStatus, type CoupleStatus, type BankStatus,
  type ImportPreview,
} from '../lib/api.js';
import { useApp } from '../store/app.js';
import { Money } from '../components/Money.js';
import { Icon } from '../components/Icon.js';
import { Sheet } from '../components/Sheet.js';
import { t } from '../lib/i18n.js';
import { formatMoney } from '../lib/format.js';

const ACCOUNT_TYPES = ['checking', 'savings', 'cash', 'credit_card', 'investment', 'liability', 'custom'] as const;

/** Accounts, settings, connected services and data ownership. §50-51 §91-93 §146-147 */
export function Profile() {
  const { user, preferences, accounts, refreshAccounts, refreshDashboard, setPreferences, signOut, toast } = useApp();
  const [google, setGoogle] = useState<GoogleStatus | null>(null);
  const [family, setFamily] = useState<FamilyStatus | null>(null);
  const [couple, setCouple] = useState<CoupleStatus | null>(null);
  const [bank, setBank] = useState<BankStatus | null>(null);
  const [addingAccount, setAddingAccount] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [gmailSheet, setGmailSheet] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    api.google.status().then(setGoogle).catch(() => undefined);
    api.family.status().then(setFamily).catch(() => undefined);
    api.couple.status().then(setCouple).catch(() => undefined);
    api.bank.status().then(setBank).catch(() => undefined);
  }, []);

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    try { await action(); } finally { setBusy(null); }
  };

  return (
    <>
      <header className="app-header">
        <div className="app-header__bar">
          <span />
          <div className="app-header__title">פרופיל</div>
          <span />
        </div>
        <p className="app-header__sub">{user?.email}</p>
      </header>

      <main className="screen stack">
        {/* ------------------------------------------------------ accounts */}
        <section className="card" aria-label="חשבונות">
          <div className="card-head">
            <h2>חשבונות</h2>
            <button className="card-link inline-target" onClick={() => setAddingAccount(true)}>הוספה</button>
          </div>
          {accounts.length === 0 ? (
            <p className="label">עדיין אין חשבונות. הוסף חשבון כדי להתחיל.</p>
          ) : (
            <div className="list">
              {accounts.map((account) => (
                <div className="tx-row" key={account.id}>
                  <span className="tx-row__icon" style={{ background: 'var(--primary-soft)', color: 'var(--primary)' }}>
                    <Icon name={accountIcon(account.type)} size={18} />
                  </span>
                  <span className="tx-row__body">
                    <span className="tx-row__name">{account.name}</span>
                    <span className="tx-row__meta">
                      {t.accountTypes[account.type] ?? account.type}
                      {account.isSpendable ? ' · זמין להוצאה' : ''}
                    </span>
                  </span>
                  <span className="tx-row__amount money">
                    <Money value={account.balance} size="inherit" tone={account.balance < 0 ? 'negative' : 'inherit'} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ---------------------------------------------------------- family */}
        <FamilySection family={family} setFamily={setFamily} busy={busy} run={run} />

        <CoupleSection couple={couple} setCouple={setCouple} busy={busy} run={run} />

        {/* ------------------------------------------------------ bank data */}
        <BankSection bank={bank} setBank={setBank} busy={busy} run={run} />

        {/* ------------------------------------------------------ settings */}
        <section className="card" aria-label="הגדרות">
          <div className="card-head"><h2>הגדרות</h2></div>
          <div className="stack">
            <div className="field">
              <span className="field__label">מראה</span>
              <div className="segmented" role="group" aria-label="ערכת נושא">
                {(['system', 'light', 'dark'] as const).map((theme) => (
                  <button
                    key={theme}
                    aria-pressed={(preferences?.theme ?? 'system') === theme}
                    onClick={() => void setPreferences({ theme })}
                  >
                    {theme === 'system' ? 'מערכת' : theme === 'light' ? 'בהיר' : 'כהה'}
                  </button>
                ))}
              </div>
            </div>

            <ToggleRow
              label="הסתרת סכומים"
              hint="מטשטש את כל הסכומים על המסך"
              checked={preferences?.hideAmounts ?? false}
              onChange={(value) => void setPreferences({ hideAmounts: value })}
            />
            <ToggleRow
              label="תובנות חכמות"
              hint="החישובים תמיד עובדים גם בלי זה"
              checked={preferences?.aiEnabled ?? true}
              onChange={(value) => void setPreferences({ aiEnabled: value })}
            />

            <NumberRow
              label="כרית ביטחון"
              hint="סכום שלא ייחשב כפנוי להוצאה"
              value={preferences?.safetyBuffer ?? 0}
              onSave={(value) => void setPreferences({ safetyBuffer: value })}
            />
            <NumberRow
              label="חיסכון חודשי מתוכנן"
              hint="מופרש לפני חישוב מה פנוי"
              value={preferences?.reservedSavingsMonthly ?? 0}
              onSave={(value) => void setPreferences({ reservedSavingsMonthly: value })}
            />
            <div className="between">
              <span>
                <span style={{ display: 'block', fontWeight: 600, fontSize: 14 }}>יום המשכורת</span>
                <span className="label--tiny">קובע את הטווח של “פנוי להוצאה”</span>
              </span>
              <select
                className="select"
                style={{ width: 90, height: 40 }}
                value={preferences?.paydayDayOfMonth ?? ''}
                onChange={(e) => void setPreferences({ paydayDayOfMonth: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">אוטומטי</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* --------------------------------------------- connected services */}
        <section className="card" aria-label="שירותים מחוברים">
          <div className="card-head"><h2>שירותים מחוברים</h2></div>
          {!google ? (
            <p className="label">טוען…</p>
          ) : (
            <div className="stack">
              <div className="between">
                <span>
                  <span style={{ display: 'block', fontWeight: 600, fontSize: 14 }}>Google</span>
                  <span className="label--tiny">{google.connected ? google.email : 'לא מחובר'}</span>
                </span>
                {google.connected ? (
                  <button
                    className="chip"
                    onClick={() => run('disconnect', async () => {
                      const result = await api.google.disconnect();
                      toast(result.message);
                      setGoogle(await api.google.status());
                    })}
                    disabled={busy === 'disconnect'}
                  >
                    ניתוק
                  </button>
                ) : (
                  <a className="chip" href={api.auth.googleUrl('/profile')}>חיבור</a>
                )}
              </div>

              <ServiceRow label="כניסה עם Google" on={google.services.login} />
              <ServiceRow
                label="גיבוי ל־Drive"
                on={google.services.driveBackup}
                connectHref={api.google.connectUrl('connect_drive')}
                action={google.services.driveBackup ? {
                  label: busy === 'backup' ? 'מגבה…' : 'גבה עכשיו',
                  run: () => run('backup', async () => {
                    try {
                      const result = await api.google.backup();
                      toast(`הגיבוי נשמר: ${result.filename}`, { tone: 'positive' });
                      setGoogle(await api.google.status());
                    } catch {
                      toast('הגיבוי נכשל. הנתונים שלך במקום.', { tone: 'danger' });
                    }
                  }),
                } : undefined}
              />
              <ServiceRow
                label="ייצוא ל־Sheets"
                on={google.services.sheetsExport}
                connectHref={api.google.connectUrl('connect_sheets')}
                action={google.services.sheetsExport ? {
                  label: busy === 'sheets' ? 'מייצא…' : 'ייצוא',
                  run: () => run('sheets', async () => {
                    try {
                      const result = await api.google.exportSheets();
                      toast('הייצוא נוצר', { tone: 'positive', action: { label: 'פתח', run: () => window.open(result.url, '_blank') } });
                    } catch {
                      toast('הייצוא נכשל. שום נתון לא השתנה.', { tone: 'danger' });
                    }
                  }),
                } : undefined}
              />
              <ServiceRow
                label="קבלות מ־Gmail"
                on={google.services.gmailReceipts}
                note="קריאה בלבד — כל מועמד עובר בדיקה ואישור ידני לפני שהוא הופך לתנועה"
                connectHref={api.google.connectUrl('connect_gmail_receipts')}
                action={google.services.gmailReceipts ? { label: 'סריקה', run: () => setGmailSheet(true) } : undefined}
              />

              {google.lastBackup && (
                <p className="label--tiny">
                  גיבוי אחרון: {new Date(google.lastBackup.at).toLocaleString('he-IL')}
                </p>
              )}
              {google.connected && (
                <p className="label--tiny">
                  ניתוק Google לא מוחק נתונים. הנתונים שכבר נשמרו במערכת יישארו.
                </p>
              )}
            </div>
          )}
        </section>

        {/* ------------------------------------------------ data & privacy */}
        <section className="card" aria-label="נתונים ופרטיות">
          <div className="card-head"><h2>נתונים ופרטיות</h2></div>
          <div className="stack">
            <a className="btn btn--block" href={api.privacy.exportUrl()}>
              <Icon name="arrow-down" size={18} /> ייצוא כל הנתונים
            </a>
            <button
              className="btn btn--block"
              onClick={() => run('history', async () => {
                if (!confirm('למחוק את כל היסטוריית התנועות? החשבונות יישארו.')) return;
                await api.privacy.deleteHistory();
                toast('היסטוריית התנועות נמחקה');
                await Promise.all([refreshAccounts(), refreshDashboard()]);
              })}
              disabled={busy === 'history'}
            >
              מחיקת היסטוריה פיננסית
            </button>
            <button className="btn btn--danger btn--block" onClick={() => setDeleting(true)}>
              מחיקת חשבון
            </button>
            <button className="btn btn--ghost btn--block" onClick={() => void signOut()}>יציאה</button>
          </div>
        </section>
      </main>

      <AddAccountSheet
        open={addingAccount}
        onClose={() => setAddingAccount(false)}
        onCreated={() => { setAddingAccount(false); void refreshAccounts(); void refreshDashboard(); }}
      />
      <DeleteAccountSheet open={deleting} email={user?.email ?? ''} onClose={() => setDeleting(false)} />
      <GmailReceiptsSheet open={gmailSheet} onClose={() => setGmailSheet(false)} accounts={accounts} />
    </>
  );
}

/**
 * Gmail → candidate rows → the SAME preview/commit pipeline a CSV import
 * uses (dedup, category suggestion, manual review). A scan never creates a
 * transaction by itself — the user picks which rows to import here. §53 §54
 */
function GmailReceiptsSheet({
  open, onClose, accounts,
}: { open: boolean; onClose: () => void; accounts: Account[] }) {
  const { toast, refreshAccounts, refreshDashboard } = useApp();
  const [sinceDays, setSinceDays] = useState(90);
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? '');
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState<{ scanned: number; matched: number } | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    if (open) {
      setScanned(null); setPreview(null); setSelected(new Set());
      if (!accountId && accounts[0]) setAccountId(accounts[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const scan = async () => {
    setScanning(true);
    try {
      const result = await api.google.gmailScan(sinceDays);
      setScanned({ scanned: result.scanned, matched: result.matched });
      if (result.matched === 0 || !accountId) { setPreview(null); return; }
      const staged = await api.imports.preview({ accountId, source: 'api', rows: result.rows });
      setPreview(staged);
      setSelected(new Set(
        staged.rows.filter((r) => r.errors.length === 0 && (r.duplicate?.confidence ?? 0) < 0.8).map((r) => r.index),
      ));
    } catch {
      toast('הסריקה נכשלה. אפשר לנסות שוב.', { tone: 'danger' });
    } finally {
      setScanning(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const rows = preview.rows
        .filter((r) => selected.has(r.index))
        .map((r) => ({
          occurredAt: r.occurredAt, amount: Math.abs(r.amount), type: r.type,
          merchant: r.merchant, description: r.description, categoryId: r.suggestedCategoryId,
          externalId: r.externalId, importAnyway: true,
        }));
      const result = await api.imports.commit({ batchId: preview.batchId, accountId, rows });
      toast(`יובאו ${result.imported} תנועות${result.skipped ? `, ${result.skipped} דולגו ככפילות` : ''}`, { tone: 'positive' });
      await Promise.all([refreshAccounts(), refreshDashboard()]);
      setPreview(null); setScanned(null); onClose();
    } catch {
      toast('הייבוא נכשל. שום תנועה לא נוספה.', { tone: 'danger' });
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Sheet open={open} title="סריקת קבלות מ-Gmail" onClose={onClose}>
      <div className="stack">
        <p className="label--tiny">
          המערכת מחפשת ב-Gmail שלך הודעות ממוסדות פיננסיים ידועים או עם מילים כמו "קבלה"/"חשבונית" —
          לא את כל תיבת הדואר. כל מועמד מבוסס על סכום שנקרא ישירות מההודעה, בלי ניחוש של AI, ומופיע כאן לבדיקה
          לפני שהוא הופך לתנועה.
        </p>

        {accounts.length === 0 ? (
          <p className="label">צריך קודם ליצור חשבון.</p>
        ) : (
          <label className="field">
            <span className="field__label">ייבוא אל חשבון</span>
            <select className="select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
        )}

        <label className="field">
          <span className="field__label">כמה ימים אחורה</span>
          <select className="select" value={sinceDays} onChange={(e) => setSinceDays(Number(e.target.value))}>
            {[30, 90, 180, 365, 1095].map((d) => <option key={d} value={d}>{d} ימים</option>)}
          </select>
        </label>

        <button className="btn btn--primary btn--block" disabled={scanning || !accountId} onClick={() => void scan()}>
          {scanning ? 'סורק…' : 'סריקה'}
        </button>

        {scanned && (
          <p className="label--tiny">
            נסרקו {scanned.scanned} הודעות, נמצאו {scanned.matched} מועמדים אפשריים.
          </p>
        )}

        {preview && preview.rows.length > 0 && (
          <div className="stack" style={{ maxHeight: 320, overflowY: 'auto', gap: 6 }}>
            {preview.rows.map((row) => (
              <label key={row.index} className="between" style={{ opacity: row.errors.length ? 0.5 : 1, cursor: row.errors.length ? 'default' : 'pointer' }}>
                <span className="row" style={{ gap: 8 }}>
                  <input
                    type="checkbox"
                    disabled={row.errors.length > 0}
                    checked={selected.has(row.index)}
                    onChange={(e) => setSelected((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(row.index); else next.delete(row.index);
                      return next;
                    })}
                  />
                  <span>
                    <span style={{ display: 'block', fontSize: 13 }}>{row.merchant ?? row.description ?? 'ללא שם'}</span>
                    <span className="label--tiny subtle">
                      {new Date(row.occurredAt).toLocaleDateString('he-IL')}
                      {row.duplicate ? ' · ייתכן שכבר קיים' : ''}
                      {row.errors.length ? ` · ${row.errors[0]}` : ''}
                    </span>
                  </span>
                </span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{formatMoney(row.amount)}</span>
              </label>
            ))}
            <button className="btn btn--primary btn--block" disabled={committing || selected.size === 0} onClick={() => void commit()}>
              {committing ? 'מייבא…' : `ייבוא ${selected.size} תנועות`}
            </button>
          </div>
        )}
      </div>
    </Sheet>
  );
}

function ToggleRow({
  label, hint, checked, onChange,
}: { label: string; hint?: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <div className="between">
      <span>
        <span style={{ display: 'block', fontWeight: 600, fontSize: 14 }}>{label}</span>
        {hint && <span className="label--tiny">{hint}</span>}
      </span>
      <button
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        style={{
          width: 50, height: 30, minHeight: 30, minWidth: 50, borderRadius: 999,
          background: checked ? 'var(--primary)' : 'var(--surface-sunken)',
          position: 'relative', transition: 'background 0.2s',
        }}
      >
        <span
          style={{
            position: 'absolute', top: 3, insetInlineStart: checked ? 23 : 3,
            width: 24, height: 24, borderRadius: 999, background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'inset-inline-start 0.2s',
          }}
        />
      </button>
    </div>
  );
}

function NumberRow({
  label, hint, value, onSave,
}: { label: string; hint?: string; value: number; onSave: (value: number) => void }) {
  const [draft, setDraft] = useState(String(value / 100));
  useEffect(() => { setDraft(String(value / 100)); }, [value]);

  return (
    <div className="between">
      <span>
        <span style={{ display: 'block', fontWeight: 600, fontSize: 14 }}>{label}</span>
        {hint && <span className="label--tiny">{hint}</span>}
      </span>
      <input
        className="input"
        style={{ width: 110, height: 40, textAlign: 'center', direction: 'ltr' }}
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/[^\d.,]/g, ''))}
        onBlur={() => onSave(Math.round(Number(draft.replace(',', '.')) * 100) || 0)}
        aria-label={label}
      />
    </div>
  );
}

/**
 * Always visible to every member, not just the admin — this is the
 * disclosure itself, not a settings toggle someone could miss. §DEMO
 */
function FamilySection({
  family, setFamily, busy, run,
}: {
  family: FamilyStatus | null; setFamily: (f: FamilyStatus) => void;
  busy: string | null; run: (key: string, action: () => Promise<void>) => Promise<void>;
}) {
  const { toast } = useApp();
  const [mode, setMode] = useState<'create' | 'join' | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  if (!family) return null;

  return (
    <section className="card" aria-label="משפחה">
      <div className="card-head"><h2>משפחה</h2></div>
      {family.inFamily ? (
        <div className="stack">
          <div className="between">
            <span>
              <span style={{ display: 'block', fontWeight: 600, fontSize: 14 }}>{family.name}</span>
              <span className="label--tiny">
                {family.role === 'family_admin' ? `אתה מנהל/ת המשפחה — יש לך גישת צפייה ל-${family.memberCount - 1} בני משפחה נוספים` : 'למנהל/ת המשפחה יש גישת צפייה בלבד לנתונים שלך'}
              </span>
            </span>
            {family.role === 'family_admin' && (
              <Link className="chip" to="/family">צפייה</Link>
            )}
          </div>
          {family.role === 'family_admin' && family.joinCode && (
            <div className="between">
              <span className="label--tiny">קוד הזמנה לבני משפחה נוספים</span>
              <strong style={{ direction: 'ltr', letterSpacing: 2 }}>{family.joinCode}</strong>
            </div>
          )}
          <button
            className="btn btn--ghost btn--block"
            disabled={busy === 'family_leave'}
            onClick={() => run('family_leave', async () => {
              try {
                await api.family.leave();
                setFamily({ inFamily: false });
                toast('יצאת מהמשפחה');
              } catch {
                toast('לא ניתן לצאת כרגע — אם אתה המנהל/ת, מנה מישהו אחר קודם.', { tone: 'danger' });
              }
            })}
          >
            יציאה מהמשפחה
          </button>
        </div>
      ) : (
        <div className="stack">
          <p className="label--tiny">
            שיתוף בין בני משפחה: מנהל/ת המשפחה מקבל/ת גישת צפייה בלבד (לא עריכה) לנתונים של כל השאר —
            וזה תמיד גלוי כאן, לא מוסתר משום אחד.
          </p>
          <div className="segmented" role="group">
            <button aria-pressed={mode === 'create'} onClick={() => setMode('create')}>התחלת משפחה</button>
            <button aria-pressed={mode === 'join'} onClick={() => setMode('join')}>הצטרפות עם קוד</button>
          </div>
          {mode === 'create' && (
            <div className="row" style={{ gap: 8 }}>
              <input className="input grow" placeholder="שם המשפחה" value={name} onChange={(e) => setName(e.target.value)} />
              <button
                className="btn btn--primary" disabled={!name.trim() || busy === 'family_create'}
                onClick={() => run('family_create', async () => {
                  const result = await api.family.create(name.trim());
                  setFamily({ inFamily: true, name: result.name, role: 'family_admin', memberCount: 1, joinCode: result.joinCode });
                  setName('');
                })}
              >
                יצירה
              </button>
            </div>
          )}
          {mode === 'join' && (
            <div className="row" style={{ gap: 8 }}>
              <input className="input grow" placeholder="קוד הזמנה" style={{ direction: 'ltr' }} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
              <button
                className="btn btn--primary" disabled={code.trim().length < 4 || busy === 'family_join'}
                onClick={() => run('family_join', async () => {
                  try {
                    const result = await api.family.join(code.trim());
                    setFamily({ inFamily: true, name: result.name, role: 'member', memberCount: 0, joinCode: null });
                    setCode('');
                  } catch {
                    toast('קוד לא תקין', { tone: 'danger' });
                  }
                })}
              >
                הצטרפות
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Couples: mutual, disclosed, view-only — see CoupleDashboard for the actual
 * side-by-side + combined-on-marriage screen at /couple. §DEMO
 */
function CoupleSection({
  couple, setCouple, busy, run,
}: {
  couple: CoupleStatus | null; setCouple: (c: CoupleStatus) => void;
  busy: string | null; run: (key: string, action: () => Promise<void>) => Promise<void>;
}) {
  const { toast } = useApp();
  const [mode, setMode] = useState<'invite' | 'join' | null>(null);
  const [code, setCode] = useState('');

  if (!couple) return null;

  const leave = () => run('couple_leave', async () => {
    await api.couple.leave();
    setCouple({ inCouple: false });
    toast('היציאה מהזוגיות בוצעה — שום נתון פיננסי לא נמחק ולא הוזז.');
  });

  return (
    <section className="card" aria-label="בני זוג">
      <div className="card-head"><h2>בן/בת זוג</h2></div>

      {!couple.inCouple ? (
        <div className="stack">
          <p className="label--tiny">
            הזמנת בן/בת זוג נותנת לשניכם צפייה הדדית ומלאה זה בנתונים של זה/זו — בלי יכולת לערוך.
            אחרי שתתחתנו (בהסכמת שניכם), התמונה תוצג גם מאוחדת.
          </p>
          <div className="segmented" role="group">
            <button aria-pressed={mode === 'invite'} onClick={() => setMode('invite')}>הזמנת בן/בת זוג</button>
            <button aria-pressed={mode === 'join'} onClick={() => setMode('join')}>הצטרפות עם קוד</button>
          </div>
          {mode === 'invite' && (
            <button
              className="btn btn--primary btn--block" disabled={busy === 'couple_invite'}
              onClick={() => run('couple_invite', async () => {
                const result = await api.couple.invite();
                setCouple({ inCouple: true, status: 'pending', joinCode: result.joinCode });
              })}
            >
              יצירת קוד הזמנה
            </button>
          )}
          {mode === 'join' && (
            <div className="row" style={{ gap: 8 }}>
              <input className="input grow" placeholder="קוד הזמנה" style={{ direction: 'ltr' }} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
              <button
                className="btn btn--primary" disabled={code.trim().length < 4 || busy === 'couple_join'}
                onClick={() => run('couple_join', async () => {
                  try {
                    await api.couple.join(code.trim());
                    const status = await api.couple.status();
                    setCouple(status);
                    setCode('');
                  } catch {
                    toast('קוד לא תקין', { tone: 'danger' });
                  }
                })}
              >
                הצטרפות
              </button>
            </div>
          )}
        </div>
      ) : couple.status === 'pending' ? (
        <div className="stack">
          <p className="label--tiny">ממתין/ה שבן/בת הזוג יצטרפו עם הקוד:</p>
          <div className="between">
            <strong style={{ direction: 'ltr', letterSpacing: 2 }}>{couple.joinCode}</strong>
          </div>
          <button className="btn btn--ghost btn--block" disabled={busy === 'couple_leave'} onClick={leave}>
            ביטול ההזמנה
          </button>
        </div>
      ) : (
        <div className="stack">
          <div className="between">
            <span>
              <span style={{ display: 'block', fontWeight: 600, fontSize: 14 }}>{couple.partnerName ?? 'בן/בת הזוג'}</span>
              <span className="label--tiny">
                {couple.status === 'married' ? 'נשואים — התמונה הפיננסית מוצגת גם מאוחדת'
                  : couple.status === 'marriage_pending' ? (couple.proposedByMe ? 'ממתין/ה לאישור בן/בת הזוג' : 'בן/בת הזוג הציע/ה נישואין')
                  : 'צפייה הדדית ומלאה, בלי עריכה'}
              </span>
            </span>
            <Link className="chip" to="/couple">צפייה</Link>
          </div>

          {couple.status === 'together' && (
            <button
              className="btn btn--block" disabled={busy === 'couple_propose'}
              onClick={() => run('couple_propose', async () => {
                await api.couple.proposeMarriage();
                setCouple({ ...couple, status: 'marriage_pending', proposedByMe: true });
                toast('הצעת הנישואין נשלחה — מחכים לאישור בן/בת הזוג');
              })}
            >
              הצעת נישואין
            </button>
          )}
          {couple.status === 'marriage_pending' && !couple.proposedByMe && (
            <button
              className="btn btn--primary btn--block" disabled={busy === 'couple_confirm'}
              onClick={() => run('couple_confirm', async () => {
                await api.couple.confirmMarriage();
                setCouple({ ...couple, status: 'married', marriedAt: Date.now() });
                toast('מזל טוב! 🎉 התמונה הפיננסית שלכם תוצג גם מאוחדת', { tone: 'positive' });
              })}
            >
              אישור נישואין
            </button>
          )}

          <button className="btn btn--ghost btn--block" disabled={busy === 'couple_leave'} onClick={leave}>
            {couple.status === 'married' ? 'ניתוק הזוגיות' : 'יציאה מהזוגיות'}
          </button>
        </div>
      )}
    </section>
  );
}

/** Bank / credit-card live sync — an Open Banking provider only, never a password form. §52 */
function BankSection({
  bank, setBank, busy, run,
}: {
  bank: BankStatus | null; setBank: (b: BankStatus) => void;
  busy: string | null; run: (key: string, action: () => Promise<void>) => Promise<void>;
}) {
  const { toast } = useApp();
  if (!bank) return null;

  return (
    <section className="card" aria-label="חיבור בנק">
      <div className="card-head"><h2>בנק וכרטיסי אשראי</h2></div>
      {!bank.configured ? (
        <p className="label--tiny">
          חיבור לבנק בלייב עדיין לא הוגדר בסביבה הזו — הוא ידרוש ספק Open Banking מורשה,
          לא סיסמת בנק. אפשר תמיד לייבא תנועות מקובץ CSV שהבנק מייצא.
        </p>
      ) : bank.connections.length === 0 ? (
        <div className="stack">
          <p className="label--tiny">מתחבר דרך {bank.providerName} — האישור ניתן אצל הספק, לא אצלנו. סיסמת הבנק שלך אף פעם לא מגיעה לשרת שלנו.</p>
          <a className="btn btn--primary btn--block" href={api.bank.connectUrl()}>חיבור חשבון בנק</a>
        </div>
      ) : (
        <div className="stack">
          {bank.connections.map((conn) => (
            <div className="between" key={conn.id}>
              <span>
                <span style={{ display: 'block', fontSize: 14 }}>{conn.label ?? bank.providerName}</span>
                <span className="label--tiny">
                  {conn.status === 'active'
                    ? conn.lastSyncedAt ? `סונכרן לאחרונה ${new Date(conn.lastSyncedAt).toLocaleString('he-IL')}` : 'טרם סונכרן'
                    : `שגיאה: ${conn.lastError ?? 'לא ידוע'}`}
                </span>
              </span>
              <button
                className="chip"
                onClick={() => run(`bank_disconnect_${conn.id}`, async () => {
                  await api.bank.disconnect(conn.id);
                  setBank({ ...bank, connections: bank.connections.filter((c) => c.id !== conn.id) });
                })}
                disabled={busy === `bank_disconnect_${conn.id}`}
              >
                ניתוק
              </button>
            </div>
          ))}
          {bank.coverage.accounts.length > 0 && (
            <div className="stack" style={{ gap: 2 }}>
              <span className="label--tiny">
                כיסוי היסטורי: {bank.coverage.overallScore}% — המערכת יודעת כמה היא באמת מכירה אותך, לא רק כמה זמן החשבון מחובר.
              </span>
              {bank.coverage.accounts.map((a) => (
                <span key={a.id} className="label--tiny subtle">
                  {a.name}: {a.coverageDays} ימי היסטוריה ({a.score}%)
                </span>
              ))}
            </div>
          )}
          <button
            className="btn btn--block"
            disabled={busy === 'bank_sync'}
            onClick={() => run('bank_sync', async () => {
              const result = await api.bank.sync();
              toast(`עודכנו ${result.transactionsAdded} תנועות חדשות`, { tone: 'positive' });
            })}
          >
            {busy === 'bank_sync' ? 'מסנכרן…' : 'סנכרון עכשיו'}
          </button>
        </div>
      )}
    </section>
  );
}

function ServiceRow({
  label, on, note, connectHref, action,
}: {
  label: string; on: boolean; note?: string; connectHref?: string;
  action?: { label: string; run: () => void };
}) {
  return (
    <div className="between">
      <span className="row" style={{ gap: 8 }}>
        <span style={{ color: on ? 'var(--positive)' : 'var(--text-3)' }}>
          <Icon name={on ? 'check' : 'close'} size={16} />
        </span>
        <span>
          <span style={{ display: 'block', fontSize: 14 }}>{label}</span>
          {note && <span className="label--tiny">{note}</span>}
        </span>
      </span>
      {action ? (
        <button className="chip" onClick={action.run}>{action.label}</button>
      ) : !on && connectHref ? (
        <a className="chip" href={connectHref}>הפעלה</a>
      ) : null}
    </div>
  );
}

function AddAccountSheet({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const { toast } = useApp();
  const [name, setName] = useState('');
  const [type, setType] = useState<(typeof ACCOUNT_TYPES)[number]>('checking');
  const [balance, setBalance] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setName(''); setType('checking'); setBalance(''); } }, [open]);

  const save = async () => {
    if (!name.trim()) { toast('צריך שם לחשבון', { tone: 'danger' }); return; }
    setSaving(true);
    try {
      await api.accounts.create({
        name: name.trim(),
        type,
        balance: Math.round(Number(balance.replace(',', '.')) * 100) || 0,
      });
      onCreated();
    } catch {
      toast(t.errors.save, { tone: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} title="חשבון חדש" onClose={onClose}>
      <div className="stack">
        <label className="field">
          <span className="field__label">שם</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="לדוגמה: עו״ש" autoFocus />
        </label>
        <label className="field">
          <span className="field__label">סוג</span>
          <select className="select" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            {ACCOUNT_TYPES.map((value) => (
              <option key={value} value={value}>{t.accountTypes[value] ?? value}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">יתרה נוכחית</span>
          <input
            className="input"
            inputMode="decimal"
            value={balance}
            onChange={(e) => setBalance(e.target.value.replace(/[^\d.,-]/g, ''))}
          />
        </label>
        <p className="label--tiny">
          חיסכון והשקעות לא ייחשבו כ״פנוי להוצאה״, אבל ייספרו בשווי הנקי.
        </p>
        <button className="btn btn--primary btn--block" onClick={save} disabled={saving}>
          {saving ? 'שומר…' : t.common.save}
        </button>
      </div>
    </Sheet>
  );
}

/** §92 — warning, re-authentication, typed confirmation, delete. */
function DeleteAccountSheet({ open, email, onClose }: { open: boolean; email: string; onClose: () => void }) {
  const { toast, signOut } = useApp();
  const [typed, setTyped] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setTyped(''); setConfirmEmail(''); } }, [open]);

  const ready = typed === 'DELETE' && confirmEmail.trim().toLowerCase() === email.toLowerCase();

  const remove = async () => {
    setBusy(true);
    try {
      await api.privacy.deleteAccount(confirmEmail.trim());
      await signOut();
    } catch {
      toast('המחיקה לא בוצעה. בדוק את הפרטים ונסה שוב.', { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} title="מחיקת חשבון" onClose={onClose}>
      <div className="stack">
        <div className="card" style={{ background: 'var(--negative-soft)', boxShadow: 'none' }}>
          <p style={{ color: 'var(--negative)', fontWeight: 600 }}>הפעולה הזו בלתי הפיכה.</p>
          <p className="label" style={{ marginTop: 6 }}>
            כל החשבונות, התנועות, היעדים והגיבויים יימחקו. מומלץ לייצא את הנתונים לפני כן.
          </p>
        </div>
        <label className="field">
          <span className="field__label">הקלד את כתובת המייל שלך</span>
          <input className="input" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder={email} />
        </label>
        <label className="field">
          <span className="field__label">הקלד DELETE כדי לאשר</span>
          <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" />
        </label>
        <button className="btn btn--danger btn--block" onClick={remove} disabled={!ready || busy}>
          {busy ? 'מוחק…' : 'מחיקה סופית'}
        </button>
      </div>
    </Sheet>
  );
}

function accountIcon(type: string) {
  switch (type) {
    case 'checking': return 'wallet' as const;
    case 'savings': return 'piggy' as const;
    case 'cash': return 'wallet' as const;
    case 'credit_card': return 'credit-card' as const;
    case 'investment': return 'chart' as const;
    default: return 'dots' as const;
  }
}

export const _unused = { formatMoney };
